import { useMutation, useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { apiClient } from '../services/apiClient';

export type SupportRequestStatus = 'new' | 'resolved' | 'archived';

export interface SupportRequestSummary {
  id: string;
  organizationId: string;
  organizationName: string | null;
  contactEmail: string;
  category: string;
  message: string;
  status: SupportRequestStatus;
  attachmentCount: number;
  createdAt: string;
  resolvedAt: string | null;
}

export interface SupportRequestAttachment {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  url: string;
}

export interface SupportRequestDetail {
  id: string;
  organizationId: string;
  organizationName: string | null;
  userId: string;
  contactEmail: string;
  category: string;
  message: string;
  marketingOptIn: boolean;
  status: SupportRequestStatus;
  resolutionNotes: string | null;
  createdAt: string;
  resolvedAt: string | null;
  attachments: SupportRequestAttachment[];
}

export interface SupportRequestPage {
  items: SupportRequestSummary[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface CustomerRequestsQuery {
  status?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export function getCustomerRequests(query: CustomerRequestsQuery): Promise<SupportRequestPage> {
  return apiClient.get<SupportRequestPage>('/admin/support/requests', {
    status: query.status || undefined,
    search: query.search || undefined,
    page: query.page ?? 1,
    pageSize: query.pageSize ?? 20,
  });
}

export function getCustomerRequest(id: string): Promise<SupportRequestDetail> {
  return apiClient.get<SupportRequestDetail>(`/admin/support/requests/${id}`);
}

export function updateCustomerRequest(
  id: string,
  status: SupportRequestStatus,
  resolutionNotes?: string,
): Promise<void> {
  return apiClient.patch<void>(`/admin/support/requests/${id}`, { status, resolutionNotes });
}

export function useCustomerRequests(
  query: CustomerRequestsQuery,
  options?: Partial<UseQueryOptions<SupportRequestPage>>,
) {
  return useQuery<SupportRequestPage>({
    queryKey: ['admin-customer-requests', query.status, query.search, query.page, query.pageSize],
    queryFn: () => getCustomerRequests(query),
    ...options,
  });
}

export function useCustomerRequest(
  id: string | null,
  options?: Partial<UseQueryOptions<SupportRequestDetail>>,
) {
  return useQuery<SupportRequestDetail>({
    queryKey: ['admin-customer-request', id],
    queryFn: () => getCustomerRequest(id!),
    enabled: !!id,
    ...options,
  });
}

export function useUpdateCustomerRequest() {
  return useMutation({
    mutationFn: ({ id, status, resolutionNotes }: { id: string; status: SupportRequestStatus; resolutionNotes?: string }) =>
      updateCustomerRequest(id, status, resolutionNotes),
  });
}
